import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { SftpImportConfigurationWorkflow, ImportType } from '../../../store/Models/Api/SftpImportModels';
import { ValidationErrorPopup } from '../components/ValidationErrorPopup';
import { ColumnMappingHelper } from '../helpers/ColumnMappingHelper';
import { AppServices } from '../../../AppServices';
import { ColumnMappingDisplaySection } from '../sections/ColumnMappingDisplaySection';
import ColumnMappingFileUploadSection from '../sections/ColumnMappingFileUploadSection';

enum PopupType {
    None = 'none',
    ValidationError = 'validation-error',
    ConflictWarning = 'conflict-warning',
    FinalWarning = 'final-warning'
}

interface ColumnMappingStepLocalProps {
    goToNextStep: () => void;
    goToPrevStep: () => void;
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
}

interface ColumnMappingStepStateProps {
    appServices: AppServices;
}

type ColumnMappingStepProps = ColumnMappingStepLocalProps & ColumnMappingStepStateProps & ToxnotComponentProperties;

interface ColumnMappingStepState {
    showValidationPopup: boolean;
    validationMessage: string;
    popupType: PopupType;
    popupHeader: string;
}

class ColumnMappingStepComponent extends ToxnotComponent<ColumnMappingStepProps, ColumnMappingStepState> {
    
    constructor(props: ColumnMappingStepProps) {
        super(props);
        this.state = {
            showValidationPopup: false,
            validationMessage: '',
            popupType: PopupType.None,
            popupHeader: ''
        };
    }

    // Helper methods for popup configuration
    private getPopupConfig(popupType: PopupType) {
        switch (popupType) {
            case PopupType.ConflictWarning:
                return {
                    showCancelButton: true,
                    okButtonText: this.localizeString("Proceed Anyway"),
                    canProceed: true
                };
            case PopupType.FinalWarning:
                return {
                    showCancelButton: true,
                    okButtonText: this.localizeString("Proceed"),
                    canProceed: true
                };
            default:
                return {
                    showCancelButton: false,
                    okButtonText: this.localizeString("OK"),
                    canProceed: false
                };
        }
    }

    private showPopup(popupType: PopupType, message: string, header: string) {
        this.setState({
            showValidationPopup: true,
            validationMessage: this.localizeString(message),
            popupType: popupType,
            popupHeader: this.localizeString(header)
        });
    }

    private handleNextStep = async () => {
        const { configData, appServices } = this.props;

        const shouldValidateBom = configData.importType === ImportType.Products || configData.importType === ImportType.Materials;
        const shouldValidateSupplier = configData.importType === ImportType.Suppliers;
        // Use the centralized validator
        const validationResult = ColumnMappingHelper.validateAll(configData, shouldValidateBom);

        
        if (!validationResult.isValid) {
            this.showPopup(PopupType.ValidationError, validationResult.errorMessage!, "Validation Error");
            return;
        }
        if(shouldValidateBom){
            // Check if external Db Id is required and skipped
            const notExternalDbIdResult = await ColumnMappingHelper
                .validateExternalDbIdForMapping(configData.spreadsheetHeadersToColumnIdMappings);

            // Check if the existing configs has any conflicts with new config's columns        
            const conflictResult = await ColumnMappingHelper
                .validateNewMapping(
                    configData,
                    appServices
                );

            if (!notExternalDbIdResult.isValid) {
                this.showPopup(PopupType.ValidationError, notExternalDbIdResult.errorMessage!, "Validation Error");
                return;
            }
            else if (!conflictResult.isValid) {
                this.showPopup(PopupType.ConflictWarning, conflictResult.errorMessage!, "Configuration Conflict Warning");
                return; // Stop execution here, wait for user to close popup
            }
        }

        if(shouldValidateSupplier){
            const conflictResult = await ColumnMappingHelper
                .validateNewMapping(
                    configData,
                    appServices
                );

            if (!conflictResult.isValid) {
                this.showPopup(PopupType.ConflictWarning, conflictResult.errorMessage!, "Configuration Conflict Warning");
                return; // Stop execution here, wait for user to close popup
            }
        }

        // All validations passed - show the final warning popup
        this.showFinalWarningPopup();
    };

    private showFinalWarningPopup = () => {
        this.showPopup(
            PopupType.FinalWarning, 
            "Please make sure the format and order of data column in your SFTP import spreadsheet match exactly with your configuration here.", 
            "Warning"
        );
    };

    private closeValidationPopup = () => {
        const { popupType } = this.state;
        
        this.setState({
            showValidationPopup: false,
            validationMessage: '',
            popupType: PopupType.None,
            popupHeader: ''
        });

        // Handle popup flow based on popup type
        switch (popupType) {
            case PopupType.ConflictWarning:
                // After conflict resolution, show the final warning
                this.showFinalWarningPopup();
                break;
            case PopupType.FinalWarning:
                // Proceed to next step
                this.props.goToNextStep();
                break;
            default:
                // For validation errors, just close the popup (stay on current page)
                break;
        }
    };

    private cancelValidationPopup = () => {
        // For cancel, just close the popup without proceeding
        this.setState({
            showValidationPopup: false,
            validationMessage: '',
            popupType: PopupType.None,
            popupHeader: ''
        });
    };

    render() {
        const { configData, onConfigChange } = this.props;
        const { showValidationPopup, validationMessage, popupHeader, popupType } = this.state;
        
        const popupConfig = this.getPopupConfig(popupType);

        return (
            <div>
                <h3>{this.localizeString("Column Settings")}</h3>
                
                {/* File Upload Section */}
                <ColumnMappingFileUploadSection
                    configData={configData}
                    onConfigChange={onConfigChange}
                />

                <div className="alert alert-info">
                    {this.localizeString("If you want to configure any columns in your BOM data file as custom columns, please create the custom columns in your account first, before conducting data column mapping below.")} <br /> 
                    {this.localizeString("To do so, navigate to Templates > Manage BOM Columns.")}
                </div>

                <div className="alert alert-info">
                    {this.localizeString("Configure your imported columns by selecting which 3E Exchange columns each should be written to. Use the drop downs to assign a column to a 3E Exchange field. To skip a column in import, select \"Skip this column\" in the Mapping drop down. The predefined column Level is used to determine hierarchy (indenting) of sub-materials.")}
                </div>
                
                <ColumnMappingDisplaySection 
                    configData={configData}
                    onConfigChange={onConfigChange}
                    isReadOnly={false}
                    showAddButton={true}
                    style={{ marginTop: '15px' }}
                />

                <div className="text-right" style={{ marginTop: '15px' }}>
                    <button className="btn btn-default" onClick={this.props.goToPrevStep}>{this.localizeString("Back")}</button>
                    <button className="btn btn-toxnot-primary" style={{ marginLeft: "10px" }} onClick={this.handleNextStep}>{this.localizeString("Next")}</button>
                </div>
                
                {/* Validation Popup */}
                <ValidationErrorPopup 
                    show={showValidationPopup}
                    message={validationMessage}
                    header={popupHeader}
                    onClose={this.closeValidationPopup}
                    showCancel={popupConfig.showCancelButton}
                    onCancel={this.cancelValidationPopup}
                    okButtonText={popupConfig.okButtonText}
                    textClass={popupType === PopupType.ConflictWarning ? "text-danger" : "text-light"}
                    enableScroll={popupType === PopupType.ConflictWarning}
                />
            </div>
        );
    }
}

const ColumnMappingStep = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(ColumnMappingStepComponent);

export default ColumnMappingStep;
